import {
  Badge,
  confirm,
  DataTable,
  Modal,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { EditIcon, TrashIcon, UsersIcon } from "lucide-react";
import { useState } from "react";
import { RowActions } from "@/shared/ui/RowActions";
import { checklistsApi } from "../api/client";
import { useDeleteChecklistMapping } from "../api/queries";

type ChecklistMapping = {
  id: string;
  checklistId: string;
  sopId: string | null;
  sopName: string | null;
  flowId: string | null;
  flowName: string | null;
  positionId: string | null;
  positionTitle: string | null;
  departmentId: string | null;
  departmentName: string | null;
  createdAt: string;
  updatedAt: string;
};

type Employee = {
  id: string;
  name: string | null;
  email: string;
  positionTitle: string | null;
  departmentName: string | null;
};

type ChecklistMappingsTableProps = {
  data: ChecklistMapping[];
  isLoading?: boolean;
  onDelete?: (id: string) => Promise<void>;
  onEdit?: (mapping: ChecklistMapping) => void;
};

export function ChecklistMappingsTable({
  data,
  isLoading,
  onDelete,
  onEdit,
}: ChecklistMappingsTableProps) {
  const deleteMapping = useDeleteChecklistMapping();
  const [selectedMappingId, setSelectedMappingId] = useState<string | null>(
    null,
  );

  const employeesQuery = useQuery<{ data: Employee[] }>({
    queryKey: ["checklists", "mapping", selectedMappingId, "employees"],
    queryFn: () => checklistsApi.getMappingEmployees(selectedMappingId ?? ""),
    enabled: !!selectedMappingId,
  });

  const columns: TanstackReactTable.ColumnDef<ChecklistMapping>[] = [
    {
      id: "departmentName",
      header: "ພະແນກ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.departmentName || "-"}</span>
      ),
    },
    {
      id: "positionTitle",
      header: "ຕໍາແໜ່ງ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.positionTitle || "-"}</span>
      ),
    },
    {
      id: "sopName",
      header: "SOP",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.sopName || "-"}</span>
      ),
    },
    {
      id: "flowName",
      header: "Flow",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.flowName || "-"}</span>
      ),
    },
    {
      id: "employees",
      header: "ພະນັກງານ",
      cell: ({ row }) => {
        const hasMapping = row.original.positionId || row.original.departmentId;
        return (
          <div className="flex items-center gap-2">
            {hasMapping && (
              <Badge
                variant="secondary"
                className="cursor-pointer"
                onClick={() => setSelectedMappingId(row.original.id)}
              >
                <UsersIcon className="mr-1 h-3 w-3" />
                ເບິ່ງພະນັກງານ
              </Badge>
            )}
            {!hasMapping && (
              <span className="text-muted-foreground text-sm">-</span>
            )}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <RowActions
          actions={[
            ...(onEdit
              ? [
                  {
                    label: "ແກ້ໄຂ",
                    icon: <EditIcon className="h-4 w-4" />,
                    onClick: () => onEdit(row.original),
                  },
                ]
              : []),
            {
              label: "ລຶບ",
              variant: "destructive",
              icon: <TrashIcon className="h-4 w-4" />,
              onClick: async () => {
                const ok = await confirm({
                  title: "ລຶບການຜູກກັບ",
                  description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບການຜູກກັບນີ້?",
                  actionText: "ລຶບ",
                  cancelText: "ຍົກເລີກ",
                  ActionProps: {
                    variant: "destructive",
                  },
                });
                if (!ok) return;

                if (onDelete) {
                  await onDelete(row.original.id);
                } else {
                  toast.promise(deleteMapping.mutateAsync(row.original.id), {
                    loading: "ກໍາລັງລຶບ...",
                    success: "ລຶບການຜູກກັບສໍາເລັດ",
                    error: "ລຶບການຜູກກັບລົ້ມເຫຼວ",
                  });
                }
              },
            },
          ]}
        />
      ),
    },
  ];

  const selectedMapping = data.find((m) => m.id === selectedMappingId);
  const employees = employeesQuery.data?.data ?? [];

  return (
    <>
      <div className="rounded-xl border bg-card">
        <DataTable<ChecklistMapping, unknown>
          columns={columns}
          data={data}
          isLoading={isLoading}
          noDataMessage="ບໍ່ມີການຜູກກັບ"
        />
      </div>

      <Modal
        open={!!selectedMappingId}
        onOpenChange={(open) => !open && setSelectedMappingId(null)}
        title="ພະນັກງານທີ່ຕ້ອງເຮັດ Checklist ນີ້"
        size="md"
      >
        <div className="space-y-2">
          {selectedMapping && (
            <div className="mb-4 text-muted-foreground text-sm">
              {selectedMapping.positionTitle && (
                <p>ຕໍາແໜ່ງ: {selectedMapping.positionTitle}</p>
              )}
              {selectedMapping.departmentName && (
                <p>ພະແນກ: {selectedMapping.departmentName}</p>
              )}
            </div>
          )}

          {employeesQuery.isLoading ? (
            <p className="py-4 text-center text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          ) : employees.length === 0 ? (
            <p className="py-4 text-center text-muted-foreground">
              ບໍ່ມີພະນັກງານໃນຕໍາແໜ່ງຫຼືພະແນກນີ້
            </p>
          ) : (
            <div className="max-h-[400px] space-y-2 overflow-y-auto">
              {employees.map((emp: Employee) => (
                <div
                  key={emp.id}
                  className="flex items-center justify-between rounded border p-3"
                >
                  <div>
                    <p className="font-medium">{emp.name || emp.email}</p>
                    <p className="text-muted-foreground text-sm">{emp.email}</p>
                    {emp.positionTitle && (
                      <p className="text-muted-foreground text-xs">
                        {emp.positionTitle}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
